// Auto-Duck-Hüllkurve — eine Rechnung für Vorschau und Export (Audit M19).
//
// Die Vorschau blendet seit 2026-05-13 weich: 2 ms Attack, wenn eine Stimme
// einsetzt, 300 ms Release, wenn sie endet (decisions.md „Auto-Duck
// Attack/Release als linear ramps"). Der Export setzte dagegen harte Sprünge
// (`setValueAtTime`) — Premix-WAV und MP4 klangen anders als die Vorschau,
// mit Pegelsprüngen und Klicks an jeder Blockkante.
//
// `computeDuckEnvelope` beschreibt die Vorschau-Semantik als stückweise
// lineare Hüllkurve: Jeder Übergang startet beim AKTUELLEN Wert (wie die
// Vorschau, die vom momentanen Pegel aus rampt). Setzt die nächste Stimme ein,
// bevor der Release fertig ist, wird er dort abgebrochen und der Attack
// startet vom Zwischenwert — genau das, was ein neuer `duck()` in der Vorschau
// mit einem laufenden Ramp macht.
//
// Web-Audio-Hinweis: Eine `linearRampToValueAtTime`-Rampe endet an IHREM
// Zeitpunkt; ein später eingefügtes Event mit früherer Zeit würde die Rampe
// verbiegen. Deshalb kommt der Release-Abbruch hier als eigener Punkt heraus
// statt als nachträgliches `setValueAtTime`.

import {
  VIDEO_DUCKING_ATTACK_MS,
  VIDEO_DUCKING_RELEASE_MS,
} from '@shared/constants';

export interface EnvelopePoint {
  /** Sekunden auf der Timeline. Streng monoton steigend. */
  time: number;
  value: number;
}

export interface DuckEnvelopeInput {
  /** Stimm-Intervalle [start, ende] in Sekunden — sortiert und
   *  überschneidungsfrei (`mergeIntervals`). */
  intervals: ReadonlyArray<readonly [number, number]>;
  /** Pegel ohne Ducking. */
  base: number;
  /** Pegel, während eine Stimme spricht. */
  ducked: number;
  attackSec?: number;
  releaseSec?: number;
}

/** Lineare Interpolation — auch die rAF-Rampe des `<video>`-Elements
 *  (`useVideoDucking`) rechnet damit, damit beide Pfade dieselbe Kurve fahren. */
export function linearRampValue(
  from: number,
  to: number,
  elapsed: number,
  duration: number,
): number {
  if (duration <= 0 || elapsed >= duration) return to;
  if (elapsed <= 0) return from;
  return from + (to - from) * (elapsed / duration);
}

/** Stückweise lineare Duck-Hüllkurve. Erster Punkt ist immer `{0, base}`;
 *  zwischen zwei Punkten ist der Pegel linear. Ohne Intervalle (oder wenn
 *  nichts zu ducken ist) genau ein Punkt. */
export function computeDuckEnvelope(input: DuckEnvelopeInput): EnvelopePoint[] {
  const attack = Math.max(0, input.attackSec ?? VIDEO_DUCKING_ATTACK_MS / 1000);
  const release = Math.max(0, input.releaseSec ?? VIDEO_DUCKING_RELEASE_MS / 1000);
  const { base, ducked } = input;
  const pts: EnvelopePoint[] = [{ time: 0, value: base }];
  if (input.intervals.length === 0 || ducked === base) return pts;

  const push = (time: number, value: number) => {
    const last = pts[pts.length - 1];
    if (time <= last.time) {
      // Gleicher Zeitpunkt: der spätere Wert gewinnt (kein senkrechter Sprung
      // mit zwei Events auf einer Zeit).
      last.value = value;
      return;
    }
    pts.push({ time, value });
  };

  for (const [rawStart, rawEnd] of input.intervals) {
    const start = Math.max(0, rawStart);
    const end = Math.max(start, rawEnd);
    // Läuft der vorige Release (oder ein extrem kurzer Attack) noch über
    // `start` hinaus? Dann dort abbrechen und vom Zwischenwert aus weiter.
    const v = envelopeValueAt(pts, start);
    while (pts.length > 1 && pts[pts.length - 1].time > start) pts.pop();
    push(start, v);
    const attackEnd = start + attack;
    push(attackEnd, ducked);
    const releaseStart = Math.max(end, attackEnd);
    push(releaseStart, ducked);
    push(releaseStart + release, base);
  }
  return pts;
}

/** Pegel der Hüllkurve zur Zeit t (für Tests und Diagnose). */
export function envelopeValueAt(pts: ReadonlyArray<EnvelopePoint>, t: number): number {
  if (pts.length === 0) return 0;
  if (t <= pts[0].time) return pts[0].value;
  for (let i = 1; i < pts.length; i++) {
    if (t <= pts[i].time) {
      const a = pts[i - 1];
      const b = pts[i];
      return linearRampValue(a.value, b.value, t - a.time, b.time - a.time);
    }
  }
  return pts[pts.length - 1].value;
}

/** Schreibt die Hüllkurve auf einen AudioParam: erster Punkt als
 *  `setValueAtTime`, alle weiteren als `linearRampToValueAtTime`. */
export function applyEnvelope(param: AudioParam, pts: ReadonlyArray<EnvelopePoint>): void {
  if (pts.length === 0) return;
  param.setValueAtTime(pts[0].value, pts[0].time);
  for (let i = 1; i < pts.length; i++) {
    param.linearRampToValueAtTime(pts[i].value, pts[i].time);
  }
}
